import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  infoRow: {
    marginBottom: theme.spacing(0.5),
  },
  label: {
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
  },
  paymentBox: {
    padding: theme.spacing(1.5),
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    marginTop: theme.spacing(1),
  },
  totalAmount: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
    color: theme.palette.primary.main,
  },
  paidAmount: {
    color: theme.palette.success.main,
  },
  pendingAmount: {
    color: theme.palette.error.main,
  },
}));

interface PreorderOrderDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  order: any;
}

const PreorderOrderDetailsDialog: React.FC<PreorderOrderDetailsDialogProps> = ({
  open,
  onClose,
  order,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('global');

  if (!order) return null;

  console.log('Order Details:', order);
  console.log('Order Items:', order.items);
  console.log('Seller Name:', order.sellerName);
  console.log('Vendor Name:', order.vendorName);

  // Calculate payment totals
  const totalPrice = order.totalPrice || 0;
  const partialPayments = order.partialPayment || [];
  const firstPayment = partialPayments.length > 0 ? partialPayments[0] : null;
  const additionalPayments = partialPayments.slice(1);
  const totalAdditionalPayments = additionalPayments.reduce((sum: number, payment: any) => sum + payment.paymentAmount, 0);
  const totalPaid = partialPayments.reduce((sum: number, payment: any) => sum + payment.paymentAmount, 0);
  // Use the paid property to determine if fully paid
  const isFullyPaid = order.paid === true;
  const balance = isFullyPaid ? 0 : (totalPrice - totalPaid);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">
          {t('preorders.order-details-title')} - {order.orderCode || order.code}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Cliente Section */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>{t('preorders.customer')}</Typography>
          <Typography className={classes.infoRow}>
            <span className={classes.label}>{t('preorders.name')}:</span>
            {order.name || order.customerName || '-'}
          </Typography>
          <Typography className={classes.infoRow}>
            <span className={classes.label}>{t('users.email')}:</span>
            {order.email || '-'}
          </Typography>
        </div>

        <Divider />

        {/* Vendedor Section */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>{t('preorders.seller')}</Typography>
          <Typography className={classes.infoRow}>
            <span className={classes.label}>{t('preorders.name')}:</span>
            {order.sellerName || '-'}
          </Typography>
          <Typography className={classes.infoRow}>
            <span className={classes.label}>{t('preorders.store')}:</span>
            {order.vendorName || '-'}
          </Typography>
        </div>

        <Divider />

        {/* Ítems Section */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>{t('preorders.items')}</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('preorders.item')}</strong></TableCell>
                  <TableCell><strong>{t('preorders.sku')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('preorders.quantity')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('preorders.price')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('preorders.total')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items && order.items.length > 0 ? order.items.map((item: any, index: number) => {
                  const itemTotal = (item.quantity || 0) * (item.pricePerUnit || 0);
                  return (
                    <TableRow key={index}>
                      <TableCell>{item.itemName || '-'}</TableCell>
                      <TableCell>{item.sku || '-'}</TableCell>
                      <TableCell align="right">{item.quantity || 0}</TableCell>
                      <TableCell align="right">₡{(item.pricePerUnit || 0).toLocaleString()}</TableCell>
                      <TableCell align="right"><strong>₡{itemTotal.toLocaleString()}</strong></TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">{t('preorders.no-items')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <Divider />

        {/* Pagos Section */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>{t('preorders.payment-information')}</Typography>
          
          <Box className={classes.paymentBox}>
            <Typography className={classes.totalAmount}>
              {t('preorders.order-total')}: ₡{totalPrice.toLocaleString()}
            </Typography>

            {firstPayment && (
              <Box mt={2}>
                <Typography variant="body2">
                  <span className={classes.label}>{t('preorders.initial-payment')}:</span>
                  <span className={classes.paidAmount}>
                    ₡{firstPayment.paymentAmount.toLocaleString()}
                  </span>
                  {' '}({Moment(firstPayment.paymentDate).format('DD/MM/YYYY')})
                </Typography>
              </Box>
            )}

            {additionalPayments.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  {t('preorders.additional-payments')}:
                </Typography>
                {additionalPayments.map((payment: any, index: number) => (
                  <Typography key={index} variant="body2" style={{ marginLeft: 16 }}>
                    • ₡{payment.paymentAmount.toLocaleString()} - {Moment(payment.paymentDate).format('DD/MM/YYYY')}
                  </Typography>
                ))}
                <Typography variant="body2" style={{ marginTop: 8, marginLeft: 16 }}>
                  <span className={classes.label}>{t('preorders.total-payments')}:</span>
                  <span className={classes.paidAmount}>
                    ₡{totalAdditionalPayments.toLocaleString()}
                  </span>
                </Typography>
              </Box>
            )}

            <Box mt={2} pt={2} borderTop="1px solid #ddd">
              <Typography variant="body1">
                <span className={classes.label}>{t('preorders.balance')}:</span>
                <span className={isFullyPaid || balance <= 0 ? classes.paidAmount : classes.pendingAmount}>
                  ₡{balance.toLocaleString()}
                </span>
              </Typography>
              {isFullyPaid && (
                <Typography variant="caption" className={classes.paidAmount}>
                  {t('preorders.fully-paid')}
                </Typography>
              )}
            </Box>
          </Box>
        </div>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('preorders.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreorderOrderDetailsDialog;
